'use client'

import { useEffect, useRef, useCallback } from 'react'
import { trackEvent } from '@/lib/blog/analytics'
import { BLOG_CONSTANTS } from '@/lib/blog/config'

interface ScrollDepthTrackerProps {
  slug: string
}

export function ScrollDepthTracker({ slug }: ScrollDepthTrackerProps) {
  const trackedDepths = useRef<Set<number>>(new Set())
  const handlerRef = useRef<(() => void) | null>(null)

  const removeListener = useCallback(() => {
    if (handlerRef.current) {
      window.removeEventListener('scroll', handlerRef.current)
      handlerRef.current = null
    }
  }, [])

  useEffect(() => {
    trackedDepths.current.clear()

    const handleScroll = () => {
      const { scrollHeight, scrollTop, clientHeight } =
        document.documentElement
      const scrollable = scrollHeight - clientHeight
      const scrollPercent =
        scrollable > 0 ? (scrollTop / scrollable) * 100 : 0

      for (const threshold of BLOG_CONSTANTS.SCROLL_DEPTH_THRESHOLDS) {
        if (
          scrollPercent >= threshold &&
          !trackedDepths.current.has(threshold)
        ) {
          trackedDepths.current.add(threshold)
          trackEvent('scroll_depth', { depth: threshold, slug })
        }
      }

      // Self-remove once all thresholds tracked
      if (trackedDepths.current.size >= BLOG_CONSTANTS.SCROLL_DEPTH_THRESHOLDS.length) {
        removeListener()
      }
    }

    handlerRef.current = handleScroll
    window.addEventListener('scroll', handleScroll, { passive: true })
    return removeListener
  }, [slug, removeListener])

  return null
}
